import type { EChartsOption } from "echarts";
import { IStatMetric } from "apps/ai-metrics/types/aiMetrics.types";
import {
  ISprintChartData,
  sprintChartToBarData,
  AdaptedDelivery,
} from "apps/ai-metrics/utils/dashboardAdapter";
import {
  createGroupedBarOption,
  createStackedBarOption,
  createGroupedStackedBarOption,
  comparisonColorAt,
  COMPARISON_COLORS,
} from "apps/ai-metrics/utils/echartOptions";
import { SectionChart } from "apps/ai-metrics/components/shared/SectionChart/SectionChart";
import { SectionHeader } from "apps/ai-metrics/components/shared/SectionHeader/SectionHeader";
import { StatCard } from "apps/ai-metrics/components/shared/StatCard/StatCard";
import { useSectionStyles as useStyles } from "apps/ai-metrics/components/shared/useSectionStyles";
import {
  SECTION_NOTES,
  getMetricHelp,
  getChartHelp,
} from "apps/ai-metrics/config/metricHelpText";

const CARD_META = [
  {
    label: "FEATURE DELIVERY RATE",
    subtitle: "Nr. of features delivered",
    key: "featureDeliveryRate" as const,
  },
  {
    label: "RE-RELEASE FREQUENCY",
    subtitle: "Re-releases per quarter",
    key: "reReleaseFrequency" as const,
  },
  {
    label: "CODE QUALITY SCORE",
    subtitle: "Composite score (1–10)",
    key: "codeQualityScore" as const,
  },
];

const BACKEND_PENDING: IStatMetric = { value: "—", pending: true };

const formatMonthLabels = (labels: string[]): string[] => {
  const years = new Set(
    labels.map((l) => l?.slice(0, 4)).filter((y): y is string => !!y),
  );
  const multiYear = years.size > 1;
  return labels.map((label) => {
    if (!label) return label;
    const match = /^(\d{4})-(\d{2})$/.exec(label);
    if (!match) return label;
    const [, year, month] = match;
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleString(undefined, {
      month: "short",
      ...(multiYear ? { year: "numeric" } : {}),
    });
  });
};

function buildFeatureDeliveryChartOption(
  data: ISprintChartData,
  comparisonEnabled: boolean,
  teamCount: number,
): EChartsOption {
  const multiTeam = teamCount > 1;

  if (!comparisonEnabled) {
    if (multiTeam) {
      return createStackedBarOption({
        xLabels: formatMonthLabels(data.labels),
        series: data.teams.map((t) => ({
          name: t.team,
          data: t.current,
          color: t.color,
        })),
        yAxisLabel: "Features",
      });
    }
    const bar = sprintChartToBarData(data, false);
    return createGroupedBarOption({
      xLabels: formatMonthLabels(bar.labels),
      series: bar.series.map((s, i) => ({
        name: s.name,
        data: s.values,
        color: comparisonColorAt(i, bar.series.length),
        pointLabels: s.pointLabels,
      })),
      yAxisLabel: "Features",
    });
  }

  if (multiTeam) {
    return createGroupedStackedBarOption({
      xLabels: data.labels,
      teams: data.teams,
      currentLabel: data.currentLabel,
      priorLabel: data.priorLabel,
      yAxisLabel: "Features",
    });
  }
  const bar = sprintChartToBarData(data, true);
  return createGroupedBarOption({
    xLabels: bar.labels,
    series: bar.series.map((s) => ({ name: s.name, data: s.values })),
    yAxisLabel: "Features",
  });
}

function buildFeatureDeliveryLegend(
  data: ISprintChartData,
  comparisonEnabled: boolean,
  teamCount: number,
): { name: string; color: string }[] {
  const multiTeam = teamCount > 1;

  if (!comparisonEnabled) {
    if (multiTeam) {
      return data.teams.map((t) => ({ name: t.team, color: t.color }));
    }
    return [{ name: data.currentLabel, color: COMPARISON_COLORS.current }];
  }

  if (multiTeam) {
    return data.teams.map((t) => ({ name: t.team, color: t.color }));
  }
  return [
    { name: data.priorLabel, color: COMPARISON_COLORS.prior },
    { name: data.currentLabel, color: COMPARISON_COLORS.current },
  ];
}

interface IDeliverySectionProps {
  kpis?: AdaptedDelivery;
  loading?: boolean;
  error?: boolean;
  featureDeliveryByTeam?: ISprintChartData;
  featureDeliveryLoading?: boolean;
  comparisonEnabled: boolean;
  teamCount: number;
}

export const DeliverySection = ({
  kpis,
  loading,
  error,
  featureDeliveryByTeam,
  featureDeliveryLoading,
  comparisonEnabled,
  teamCount,
}: IDeliverySectionProps): JSX.Element => {
  const styles = useStyles();

  const cards: Record<string, IStatMetric> = {
    featureDeliveryRate: loading
      ? { value: "—" }
      : (kpis?.featureDeliveryRate ?? { value: "—", pending: true }),
    reReleaseFrequency: BACKEND_PENDING,
    codeQualityScore: BACKEND_PENDING,
  };

  const chartOption = featureDeliveryByTeam
    ? buildFeatureDeliveryChartOption(
        featureDeliveryByTeam,
        comparisonEnabled,
        teamCount,
      )
    : undefined;

  const legend = featureDeliveryByTeam
    ? buildFeatureDeliveryLegend(
        featureDeliveryByTeam,
        comparisonEnabled,
        teamCount,
      )
    : undefined;

  return (
    <div className={styles.section}>
      <SectionHeader title="Delivery & Progress" note={SECTION_NOTES.delivery}>
        <div className={styles.cardsRow}>
          {CARD_META.map(({ label, subtitle, key }) => (
            <StatCard
              key={label}
              label={label}
              subtitle={subtitle}
              loading={key === "featureDeliveryRate" ? loading : false}
              {...(!loading || key !== "featureDeliveryRate" ? cards[key] : {})}
              info={getMetricHelp(label)}
            />
          ))}
        </div>
        <SectionChart
          title="Feature Delivery by Team"
          option={
            chartOption
              ? { ...chartOption, legend: { show: false } }
              : undefined
          }
          loading={featureDeliveryLoading}
          emptyMessage={
            !featureDeliveryLoading && !featureDeliveryByTeam
              ? error
                ? "Failed to load data"
                : "No data for selected period"
              : undefined
          }
          legend={legend}
          info={getChartHelp("Feature Delivery by Team")}
        />
      </SectionHeader>
    </div>
  );
};
